import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Tooltip } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { LockBadgeComponent } from '../ui/upsell/lock-badge.component';
import { LicenseService } from '../api/license.service';
import { AuthService } from '../auth/auth.service';
import { ProfileService } from '../sections/profile/profile.service';
import { LicenseType } from '../api/gqty/schema.generated';
import { environment } from '../../environments/environment';
import { buildInfo } from '../../environments/build-info';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

interface NavItemConfig {
  labelKey: string;
  icon: string;
  route: string;
  /** Module that gates this item by license; absent means always available. */
  requiredModule?: LicenseType;
  /** Force-locked regardless of license (upsell-only modules without a license type). */
  alwaysLocked?: boolean;
}

interface NavItem extends NavItemConfig {
  locked: boolean;
}

@Component({
  selector: 'app-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NgTemplateOutlet,
    TranslateModule,
    Tooltip,
    ToastModule,
    LockBadgeComponent,
    ConfirmDialogModule,
  ],
  templateUrl: './shell.component.html',
})
export class ShellComponent {
  private readonly licenses = inject(LicenseService);
  private readonly auth = inject(AuthService);
  private readonly profile = inject(ProfileService);
  private readonly toast = inject(MessageService);

  readonly collapsed = signal(false);

  /** Tenant shown under the logo (badge initial + name); loaded once. */
  readonly tenantName = signal('');
  readonly tenantInitial = computed(() => this.tenantName().trim().charAt(0).toUpperCase() || 'T');

  /** Current user for the footer chip (name + role), from the auth signal. */
  readonly userName = computed(() => {
    const u = this.auth.currentUser();
    if (!u) return '';
    const last = u.lastName ? ` ${u.lastName.charAt(0)}.` : '';
    return `${u.firstName}${last}`.trim();
  });
  readonly userInitials = computed(() => {
    const u = this.auth.currentUser();
    if (!u) return '?';
    return ((u.firstName?.charAt(0) ?? '') + (u.lastName?.charAt(0) ?? '')).toUpperCase() || '?';
  });
  /** Highest-priority role, as an i18n key under `profile.roles.*` ('' if none). */
  readonly roleKey = computed(() => {
    const roles = this.auth.currentUser()?.roles ?? [];
    return ['admin', 'manager', 'operator', 'worker'].find((r) => roles.includes(r)) ?? roles[0] ?? '';
  });

  // Dev-only: the current git branch, shown as a chip under the logo. Empty in
  // production builds and whenever git was unavailable when the build-info file
  // was generated (e.g. inside the container — it is stamped from the host).
  readonly branch = environment.isDev ? buildInfo.branch : '';

  constructor() {
    void this.licenses.load();
    void this.profile.getTenantName().then((name) => this.tenantName.set(name));
  }

  async copyBranch(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.branch);
      this.toast.add({ severity: 'success', summary: 'Branch copied', detail: this.branch, life: 2000 });
    } catch {
      this.toast.add({ severity: 'error', summary: 'Could not copy branch', life: 3000 });
    }
  }

  /** Dashboard sits alone at the top, above the grouped nav. */
  private readonly dashboardConfig: NavItemConfig = {
    labelKey: 'shell.nav.dashboard',
    icon: 'home',
    route: '/dashboard',
  };

  /** The nav grouped by domain (Operations / Transportation / Insights / Business). */
  private readonly groupsConfig: {
    labelKey: string;
    /** When set, the whole section is gated by this license and its items lock together. */
    requiredModule?: LicenseType;
    items: NavItemConfig[];
  }[] = [
    {
      labelKey: 'shell.nav.group.operations',
      items: [
        { labelKey: 'shell.nav.workPackages', icon: 'work', route: '/work-packages' },
        { labelKey: 'shell.nav.resources', icon: 'group', route: '/resources' },
        {
          labelKey: 'shell.nav.planning',
          icon: 'calendar_month',
          route: '/planning',
          requiredModule: LicenseType.EVERPLAN_PLANNING,
        },
        {
          labelKey: 'shell.nav.live',
          icon: 'my_location',
          route: '/live',
          requiredModule: LicenseType.EVERPLAN_LIVE,
        },
      ],
    },
    {
      // Transportation is its own section (like Operations), gated as a whole. Built to grow —
      // Consolidations and Hubs today, more (e.g. Third Party Logistics) to come.
      labelKey: 'shell.nav.transportation',
      requiredModule: LicenseType.EVERPLAN_TRANSPORTATION,
      items: [
        {
          labelKey: 'transportation.view.groupage',
          icon: 'merge',
          route: '/transportation/consolidations',
        },
        {
          labelKey: 'transportation.view.hubs',
          icon: 'warehouse',
          route: '/transportation/hubs',
        },
      ],
    },
    {
      labelKey: 'shell.nav.group.insights',
      items: [
        { labelKey: 'shell.nav.analytics', icon: 'query_stats', route: '/analytics', alwaysLocked: true },
        { labelKey: 'shell.nav.ai', icon: 'auto_awesome', route: '/ai', alwaysLocked: true },
      ],
    },
    {
      labelKey: 'shell.nav.group.business',
      items: [
        { labelKey: 'shell.nav.customers', icon: 'contacts', route: '/customers', alwaysLocked: true },
        { labelKey: 'shell.nav.financial', icon: 'payments', route: '/financial', alwaysLocked: true },
        { labelKey: 'shell.nav.inventory', icon: 'inventory_2', route: '/inventory', alwaysLocked: true },
      ],
    },
  ];

  private readonly settingsConfig: NavItemConfig = {
    labelKey: 'shell.nav.settings',
    icon: 'settings',
    route: '/settings',
  };

  /**
   * Applies license locks: an item is locked by its own `requiredModule`/`alwaysLocked`, or by the
   * section's `groupModule` — a gated section (e.g. Transportation) locks all its items together.
   */
  private withLocks(items: NavItemConfig[], groupModule?: LicenseType): NavItem[] {
    const groupLocked = !!groupModule && !this.licenses.hasModule(groupModule);
    return items.map((item) => ({
      ...item,
      locked:
        groupLocked ||
        !!item.alwaysLocked ||
        (!!item.requiredModule && !this.licenses.hasModule(item.requiredModule)),
    }));
  }

  readonly dashboardItem = computed(() => this.withLocks([this.dashboardConfig])[0]);
  readonly navGroups = computed(() =>
    this.groupsConfig.map((g) => ({
      labelKey: g.labelKey,
      items: this.withLocks(g.items, g.requiredModule),
    })),
  );
  readonly settingsItem = computed(() => this.withLocks([this.settingsConfig])[0]);
}
